/**
 * SystemHealthPanel — Oracle, Event Bus, Redis status indicators.
 */

import { useAgentsOverview, useEventBusStats } from "@/sdk/hooks/use-agents";

function StatusRow({
  label,
  ok,
  detail,
}: {
  label: string;
  ok: boolean;
  detail: string;
}) {
  return (
    <div className="flex items-center justify-between px-3 py-1.5 text-xs">
      <div className="flex items-center gap-2">
        <span
          className={`h-2 w-2 rounded-full ${ok ? "bg-emerald-500" : "bg-red-500"}`}
        />
        <span className="text-zinc-300">{label}</span>
      </div>
      <span className="font-mono text-zinc-500">{detail}</span>
    </div>
  );
}

export function SystemHealthPanel() {
  const { data: overview } = useAgentsOverview();
  const { data: busStats } = useEventBusStats();

  const agents = overview?.agents ? Object.values(overview.agents) : [];
  const running = agents.filter((a) => a.state === "running").length;
  const oracle = overview?.oracle;
  const bus = busStats ?? overview?.event_bus;

  return (
    <div className="h-full overflow-auto bg-zinc-950">
      <div className="border-b border-zinc-800 px-3 py-1.5">
        <span className="text-[10px] uppercase tracking-wider text-zinc-600">
          System Health
        </span>
      </div>

      <StatusRow
        label="Agents"
        ok={running > 0}
        detail={`${running}/${agents.length} running`}
      />
      <StatusRow
        label="Oracle"
        ok={oracle?.state === "running"}
        detail={oracle ? `${oracle.consensus_count} consensuses` : "offline"}
      />
      <StatusRow
        label="Event Bus"
        ok={(bus?.subscribers ?? 0) > 0}
        detail={bus ? `${bus.subscribers} subs, ${bus.events_per_minute}/min` : "offline"}
      />
      <StatusRow
        label="Total Events"
        ok={(bus?.total_published ?? 0) > 0}
        detail={bus ? `${bus.total_published} published` : "—"}
      />

      {bus?.channel_counts && (
        <div className="mt-2 border-t border-zinc-800 px-3 pt-2">
          <span className="text-[10px] uppercase tracking-wider text-zinc-600">
            Event Channels
          </span>
          {Object.entries(bus.channel_counts).map(([channel, count]) => (
            <div
              key={channel}
              className="flex items-center justify-between py-0.5 text-xs"
            >
              <span className="text-zinc-400">{channel}</span>
              <span className="font-mono text-zinc-500">{count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
